import React, { useEffect, useState, useCallback } from 'react';

import { CTA } from '@vw-marketing/us-components';

import { useAuth } from '../../app/providers/AuthContext';
import { useMyVwContext } from '../../shared/hooks/use-my-vw-context';
import { useDisclaimers } from '../../shared/hooks/use-disclaimers';
import { useElementStyleOverride } from '../../shared/hooks/use-element-style-override';
import { useFeatureAppContent } from '../../shared/hooks/use-feature-app-content';
import { useTrackingManager } from '../../shared/hooks/use-tracking-manager';
import { applyTextTransformations } from '../../shared/utils/applyTextTransformations';
import { getBackgroundImages } from '../../shared/utils/image-mapping';
import { fetchScene7Image } from '../../shared/fetch-data/fetch-bg-image';
import { DisclaimerModal } from '../../shared/components/disclaimer-modal';
import { ClickableSupDisclaimer } from '../../shared/components/clickable-sup-disclaimer';
import { onVWBasicButtonClickEvent, onVWBasicLinkClickWithProcessEvent } from '../../shared/tagging/tagging-helpers';
import {
  StyledLoginContainer,
  StyledCTAWrapper,
  StyledBackgroundWrapper,
  StyledWelcomeContentWrapper,
  StyledTitleContainer,
  StyledTitleText,
  StyledAppNameText,
  StyledDescriptionContainer,
  StyledSignUpContainer,
} from './styles';

interface ProcessedImages {
  mobile: string;
  tablet: string;
  desktop: string;
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const { translationHandler } = useMyVwContext();
  const { disclaimers } = useDisclaimers();
  const { backgroundImages } = useFeatureAppContent();
  const trackingManager = useTrackingManager();
  const disclaimerIndex = '1';

  const [processedImages, setProcessedImages] = useState<ProcessedImages | null>(null);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  useElementStyleOverride(
    true,
    'regex:^StyledOverflowWrapper-sc-[a-zA-Z0-9]+$',
    { overflow: 'visible' },
  );

  const processImages = useCallback(async () => {
    setIsLoadingImages(true);

    try {
      // Get base images based on environment and CMS content
      const baseImages = getBackgroundImages(backgroundImages);

      if (baseImages) {
        // Process each image through Scene7 for optimization
        const [desktopProcessed, tabletProcessed, mobileProcessed] = await Promise.all([
          fetchScene7Image(baseImages.desktop),
          fetchScene7Image(baseImages.tablet),
          fetchScene7Image(baseImages.mobile),
        ]);

        setProcessedImages({
          desktop: desktopProcessed,
          tablet: tabletProcessed,
          mobile: mobileProcessed,
        });
      } else {
        // No images available, keep null to show gray background
        setProcessedImages(null);
      }
    } catch (error) {
      // Fallback: use base images if Scene7 processing fails
      const baseImages = getBackgroundImages(backgroundImages);
      if (baseImages) {
        setProcessedImages({
          desktop: baseImages.desktop,
          tablet: baseImages.tablet,
          mobile: baseImages.mobile,
        });
      } else {
        setProcessedImages(null);
      }
    } finally {
      setIsLoadingImages(false);
    }
  }, [backgroundImages]);

  const handleLoginClick = () => {
    // Track the login button click
    onVWBasicButtonClickEvent(trackingManager, {
      Type: 'Button',
      Name: translationHandler('login.button'),
      ItemCount: 2,
      ItemPosition: 1,
    });
    
    // Proceed with login
    login();
  };

  const handleSignUpClick = (linkName: string) => {
    // Track the sign up link click
    onVWBasicLinkClickWithProcessEvent(
      trackingManager,
      {
        Name: 'Login',
        StepName: 'Account',
        StepNumber: '1',
      }, {
        name: linkName,
      }
      
    );
    
    // Proceed with login (same action as login button)
    login();
  };

  useEffect(() => {
    processImages();
  }, [processImages]);

  return (
    <StyledLoginContainer>
      <StyledBackgroundWrapper
        id="background"
        backgroundImages={!isLoadingImages && processedImages ? processedImages : undefined}
      />
      <StyledWelcomeContentWrapper id="welcome-content">
        <StyledTitleContainer>
          <StyledTitleText>{translationHandler('login.title')}</StyledTitleText>{' '}
          <StyledAppNameText className="notranslate">
            {translationHandler('login.appName')}{' '}
            <sup>
              <ClickableSupDisclaimer disclaimerNumber={disclaimerIndex}>
                {disclaimerIndex}
              </ClickableSupDisclaimer>
            </sup>
          </StyledAppNameText>
        </StyledTitleContainer>

        <StyledDescriptionContainer>
          {applyTextTransformations(
            translationHandler('login.description'),
            ['Volkswagen'],
            [],
          )}
        </StyledDescriptionContainer>

        <StyledCTAWrapper>
          <CTA
            stretchContent
            tag="button"
            emphasis="primary"
            onClick={handleLoginClick}
          >
            {translationHandler('login.button')}
          </CTA>
        </StyledCTAWrapper>

        <StyledSignUpContainer>
          <span>{translationHandler('login.signUp.description')}</span>{' '}
          <CTA tag="a" emphasis="tertiary" onClick={()=>{
            handleSignUpClick(translationHandler('login.signUp'))
            }}>
            {translationHandler('login.signUp')}
          </CTA>
        </StyledSignUpContainer>
      </StyledWelcomeContentWrapper>

      <DisclaimerModal disclaimers={disclaimers} />
    </StyledLoginContainer>
  );
};

export default Login;